import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { NotificationManager, NotificationConfig, NotificationFilter } from './NotificationManager';

interface NotificationContextValue {
  manager: NotificationManager;
  notifications: NotificationConfig[];
  unreadCount: number;
  show: (config: Omit<NotificationConfig, 'id' | 'timestamp'>) => string;
  dismiss: (id: string) => boolean;
  dismissAll: (filter?: NotificationFilter) => number;
  markAsRead: (id: string) => boolean;
  markAllAsRead: (filter?: NotificationFilter) => number;
  info: (title: string, message: string, options?: Partial<NotificationConfig>) => string;
  success: (title: string, message: string, options?: Partial<NotificationConfig>) => string;
  warning: (title: string, message: string, options?: Partial<NotificationConfig>) => string;
  error: (title: string, message: string, options?: Partial<NotificationConfig>) => string;
  showProgress: (title: string, message: string, progress?: number, options?: Partial<NotificationConfig>) => string;
  updateProgress: (id: string, progress: number, message?: string) => boolean;
  completeProgress: (id: string, successMessage?: string) => boolean;
  subscribe: (filter: NotificationFilter, callback: (notification: NotificationConfig) => void) => string;
  unsubscribe: (subscriptionId: string) => boolean;
  getStats: () => ReturnType<NotificationManager['getStats']>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

interface NotificationProviderProps {
  children: ReactNode;
  options?: {
    maxNotifications?: number;
    defaultDuration?: number;
    vapidPublicKey?: string;
    persistNotifications?: boolean;
  };
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  options = {}
}) => {
  const [manager] = useState(() => new NotificationManager(options));
  const [notifications, setNotifications] = useState<NotificationConfig[]>([]);
  const [readNotifications, setReadNotifications] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Subscribe to all notifications
    const subscriptionId = manager.subscribe({}, (notification) => {
      setNotifications(manager.getAll());
    });

    // Load persisted notifications if enabled
    if (options.persistNotifications) {
      try {
        const stored = localStorage.getItem('ferp-notifications');
        if (stored) {
          const parsed = JSON.parse(stored);
          manager.import(parsed.notifications || []);
          setReadNotifications(new Set(parsed.readIds || []));
        }
      } catch (error) {
        console.warn('Failed to load persisted notifications:', error);
      }
    }

    // Initial load
    setNotifications(manager.getAll());

    return () => {
      manager.unsubscribe(subscriptionId);
    };
  }, [manager, options.persistNotifications]);

  // Persist notifications when they change
  useEffect(() => {
    if (options.persistNotifications) {
      try {
        const toStore = {
          notifications: notifications.slice(-50), // Keep last 50
          readIds: Array.from(readNotifications)
        };
        localStorage.setItem('ferp-notifications', JSON.stringify(toStore));
      } catch (error) {
        console.warn('Failed to persist notifications:', error);
      }
    }
  }, [notifications, readNotifications, options.persistNotifications]);

  const unreadCount = notifications.filter(n => !readNotifications.has(n.id)).length;

  const markAsRead = (id: string): boolean => {
    if (readNotifications.has(id)) return false;
    
    setReadNotifications(prev => new Set(prev).add(id));
    return true;
  };

  const markAllAsRead = (filter?: NotificationFilter): number => {
    const toMark = filter ? notifications.filter(n => manager['matchesFilter'](n, filter)) : notifications;
    const newReadIds = toMark.filter(n => !readNotifications.has(n.id)).map(n => n.id);
    
    if (newReadIds.length > 0) {
      setReadNotifications(prev => {
        const newSet = new Set(prev);
        newReadIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
    
    return newReadIds.length;
  };

  const contextValue: NotificationContextValue = {
    manager,
    notifications,
    unreadCount,
    show: (config) => manager.show(config),
    dismiss: (id) => manager.dismiss(id),
    dismissAll: (filter) => manager.dismissAll(filter),
    markAsRead,
    markAllAsRead,
    info: (title, message, options) => manager.info(title, message, options),
    success: (title, message, options) => manager.success(title, message, options),
    warning: (title, message, options) => manager.warning(title, message, options),
    error: (title, message, options) => manager.error(title, message, options),
    showProgress: (title, message, progress, options) => manager.showProgress(title, message, progress, options),
    updateProgress: (id, progress, message) => manager.updateProgress(id, progress, message),
    completeProgress: (id, successMessage) => manager.completeProgress(id, successMessage),
    subscribe: (filter, callback) => manager.subscribe(filter, callback),
    unsubscribe: (subscriptionId) => manager.unsubscribe(subscriptionId),
    getStats: () => manager.getStats()
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};